// Pure logic for the Research Pack comparison journey (Research Packs epic, frames 04-15):
// membership of shared research items, picking the packs, knowing when a comparison is allowed,
// building the comparison question and rendering the AI's markdown result (incl. tables).
// Plain module — unit-testable without mounting components, like ongoingOrigin.js.

// An item (or timeline event) belongs to a pack directly (packId) or as a SHARED item referenced
// by several packs (packIds — e.g. a comparison saved into both compared packs).
export function itemInPack(item, packId) {
  if (!item || !packId) return false
  return item.packId === packId || (Array.isArray(item.packIds) && item.packIds.includes(packId))
}

// How many saved items a pack holds (its own + shared), excluding its internal write-up doc —
// and excluding a comparison saved INTO it (2026-09-15 parity audit E-1, busy-my-web#485): the
// pack card's "N items" is what was ticked into the pack, and it must not change when the pack is
// compared with another (the audit watched it go 7 → 8 on a compare-save). Same rule as the
// timeline's pack nodes (lib/storySteps `isPackNodeItem`), so the card and the rail agree.
// This is a COUNT of ticked-in items — what the pack card's "N items" line says. It is NOT the
// test for "is this pack empty": that is `packHasResearch` below.
export function packItemCount(research, packId) {
  return (research || []).filter(r => r && r.kind !== 'doc' && r.kind !== 'comparison' && itemInPack(r, packId)).length
}

/**
 * The words a pack's write-up DOCUMENT actually shows.
 *
 * Kept as a regex strip rather than a DOM parse so this module stays pure (no `document`), like the
 * rest of the file. An <img> becomes a visible token: a write-up that is only pictures is still a
 * write-up with something in it.
 */
export function docText(html) {
  return String(html || '')
    .replace(/<(script|style)[\s\S]*?<\/\1>/gi, ' ')
    .replace(/<img\b[^>]*>/gi, ' [image] ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&lt;/gi, '<').replace(/&gt;/gi, '>').replace(/&quot;/gi, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/gi, '&')
    .replace(/\s+/g, ' ')
    .trim()
}

// The text a NEW pack's document is created with (StoryView's composePackStartDoc) — a prompt to
// write, not writing. A pack holding only this has nothing in it.
const DOC_PLACEHOLDER = /^write your research document here[\s.…]*$/i

/**
 * What a pack's own document SAYS — '' when there is no document, or when it still holds only the
 * "write your research document here…" prompt.
 */
export function packDocText(research, packId) {
  const doc = (research || []).find(r => r && r.kind === 'doc' && itemInPack(r, packId))
  const text = doc ? docText(doc.content) : ''
  return DOC_PLACEHOLDER.test(text) ? '' : text
}

/**
 * Does this pack hold research — the question every "can it be compared" answer is really asking.
 *
 * Pouria, 2026-08-18 (screenshot): a pack showing two photos, its snippets and two "Update vN"
 * sections was refused with "«A phone with 2 camera» has no saved research yet". It is not empty:
 * everything on that page is the pack's own DOCUMENT, and the count above deliberately skips the
 * document (`kind !== 'doc'`) because it is written FROM the items — which makes it exactly the
 * wrong measure of emptiness the moment the document is where the content lives. It is by design:
 * the pack page's document absorbs each later save as an "Update vN" section, the user edits it
 * there, and StoryView's own rule is that "the document IS the pack's content" (deleting a picture
 * from it deletes the item). A pack whose write-up says something has research to compare, however
 * many rows its item list happens to have.
 *
 * Items first, because that is the cheap check and the common case.
 */
export function packHasResearch(research, packId) {
  // Its own membership test rather than `packItemCount` (busy-my-web#485): the card count now
  // leaves a saved comparison out, but a pack holding only a comparison still has research in it.
  const hasItem = (research || []).some(r => r && r.kind !== 'doc' && itemInPack(r, packId))
  return hasItem || packDocText(research, packId).length > 0
}

// At least two packs per comparison — three or more are allowed (Pouria, 2026-08-16: "i need to
// be able to compare like more than 2 research packs"). There is no upper cap: the result is a
// table with one row per pack, so it grows with the selection.
export const MIN_COMPARE_PACKS = 2

// Toggle a pack in the compare selection. Uncapped by default — every ticked pack joins the
// comparison; callers may still pass a `max` if a surface needs one.
export function togglePick(picks, pack, max = Infinity) {
  const list = picks || []
  if (!pack || !pack.id) return list
  if (list.some(p => p.id === pack.id)) return list.filter(p => p.id !== pack.id)
  if (list.length >= max) return list
  return [...list, { id: pack.id, title: pack.title || 'Research pack' }]
}

// WHICH selection an answer on the picks card is about (busy-my-web#667): the pack ids, order-free,
// so "chose Compare" and "closed the card" belong to one selection and a different selection asks
// again. Empty for no packs, which no answer is ever recorded against.
export function picksKey(picks) {
  return (picks || []).map(p => p && p.id).filter(Boolean).map(String).sort().join(',')
}

// Does the action row's Compare pill show? (busy-my-web#667, Saffet: "when you select 2 research
// packs you need to select compare research pack for that compare button to show" / "I closed the
// what would you like to do next but the compare button is still on".) The pill is the card's
// answer, not the selection's: only once "Compare Research Packs" was chosen on THIS selection, and
// never after the card was closed on it. `verbsShowing` is the row's own rule (no typing, no
// creation mode…), which still holds "Clear selections" on its own.
export function compareGoShowing({ verbsShowing, key, chosenKey, dismissedKey }) {
  if (!verbsShowing || !key) return false
  return chosenKey === key && dismissedKey !== key
}

// Can these picks be compared? Two packs or more, each holding at least one saved item.
// ASSUMPTION (designer never specified an empty-pack frame): comparing a pack with no items is
// disabled with a message rather than allowed — recorded in the sprint notes.
export function canCompare(picks, research) {
  const list = picks || []
  if (list.length < MIN_COMPARE_PACKS) return { ok: false, reason: 'Select at least two Research Packs to compare.' }
  const empty = list.find(p => !packHasResearch(research, p.id))
  if (empty) return { ok: false, reason: `“${empty.title}” has no saved research yet — add items to it before comparing.` }
  return { ok: true, reason: '' }
}

/**
 * WHICH packs an offer to compare is ABOUT — the ticked ones, or, when nothing is ticked, the
 * packs the user already has open on the Work-board.
 *
 * Pouria, 2026-08-18: "make sure if we have two research pack on workboard we show the suggestion
 * for compare like when we select them." Opening two packs is already the statement that they are
 * being weighed against each other; making the user tick what is in front of them was asking for
 * the same intent twice.
 *
 * Rules, in order:
 *  • a TICK always wins — it is the stronger, explicit claim (the same precedence the Work-board's
 *    chip dedup uses). Untick back to zero and the open packs are offered again.
 *  • TWO open packs or more suggest; ONE does not. Opening a single pack is just reading it, and
 *    "Compare with web search" unasked for is a nag — the one-pack offer stays a ticked-only thing.
 *  • the suggestion is one STORY's: open packs stay on the board across navigation, so packs
 *    belonging to another ongoing are dropped when a story id is given.
 *  • the open order is kept, so the comparison is titled the way the user built it up.
 *
 * Open packs are Work-board chips ({ id: 'pack:<id>', kind: 'pack', packId, title, ongoingId });
 * what comes back is always in PICK shape ({ id, title }), so every caller downstream —
 * compareTarget, the pill, the prompt — reads one kind of list.
 *
 * @returns {Array<{id: string, title: string}>}
 */
export function suggestedPicks(picks, openPacks, ongoingId) {
  const ticked = (picks || []).filter(p => p && p.id)
  if (ticked.length) return ticked
  const open = (openPacks || [])
    .filter(p => p && p.packId && (!ongoingId || String(p.ongoingId) === String(ongoingId)))
  if (open.length < MIN_COMPARE_PACKS) return []
  const seen = new Set()
  return open.reduce((list, p) => {
    if (seen.has(p.packId)) return list
    seen.add(p.packId)
    return [...list, { id: p.packId, title: p.title || 'Research pack' }]
  }, [])
}

/**
 * WHAT a comparison compares — packs against each other, or the one picked pack against the web.
 *
 * Pouria, 2026-08-17: "we need to make sure we have at least 2 reasaech pack then show compare
 * otherwise it should be compare to internet." One picked pack used to be a dead end: the card
 * offered "Compare Research Packs" with the pill greyed out and "Select at least two…" underneath,
 * which is a suggestion the user cannot act on. There is something to compare a lone pack WITH —
 * what is online — so that is what is offered instead.
 *
 * An EMPTY pack is still a refusal in either mode: there is nothing saved in it to compare, with
 * another pack or with the internet.
 *
 * The one-pack wording is "Compare with web search" (busy-my-web#257, frame 40008199:410411 —
 * Fatemeh: keep this working name for now). It replaces "Compare to internet", which named the
 * PLACE the answer comes from rather than the act the row performs; every other suggestion in the
 * box is written as the thing that will happen, and this one now reads the same way. The word
 * "search" is also the truth of it — the row runs a web SEARCH and lands its answer in the middle,
 * it does not open the internet.
 *
 * GENERALISED past packs (#257, Fatemeh: "this rule is the same for every item type that is both
 * selectable and comparable"): the one-vs-two rule and its two labels are a property of SELECTION,
 * not of Research Packs, so the noun is a parameter. One selected → the web; two or more → each
 * other, headed by that type's plural. Packs stay the default because they are the only comparable
 * selection the app ships today — the seam is here so the next one costs a noun rather than a fork.
 *
 * @param picks the selected items ({ id, title })
 * @param research the story's saved research, for the empty check
 * @param opts.noun singular type name, e.g. 'Research Pack'
 * @param opts.plural its plural, defaulted by adding an s
 * @returns {{ mode: 'none'|'internet'|'packs', ok: boolean, reason: string, label: string }}
 */
export const WEB_COMPARE_LABEL = 'Compare with web search'

export function compareTarget(picks, research, opts = {}) {
  const noun = opts.noun || 'Research Pack'
  const plural = opts.plural || `${noun}s`
  const together = `Compare ${plural}`
  const list = (picks || []).filter(p => p && p.id)
  if (!list.length) {
    return { mode: 'none', ok: false, reason: `Pick a ${noun} to compare.`, label: together }
  }
  const empty = list.find(p => !packHasResearch(research, p.id))
  if (empty) {
    return {
      mode: list.length < MIN_COMPARE_PACKS ? 'internet' : 'packs',
      ok: false,
      reason: `“${empty.title}” has no saved research yet — add items to it before comparing.`,
      label: list.length < MIN_COMPARE_PACKS ? WEB_COMPARE_LABEL : together,
    }
  }
  if (list.length < MIN_COMPARE_PACKS) {
    return { mode: 'internet', ok: true, reason: '', label: WEB_COMPARE_LABEL }
  }
  return { mode: 'packs', ok: true, reason: '', label: together }
}

/**
 * The Timeline's SELECTION step (#257, frame 40008185:406044): with items ticked, the rail ends in
 * a filter node — "Research packs / 2 items selected" — the selected items listed under it, and
 * "Clear selection" beneath them. It is the third place a selection can be read and acted on, after
 * the Briefing and the Work-board (Fatemeh, step 3), and the only one that says it in the story's
 * own chronological place.
 *
 * Pure so the rail's wording is testable without the 2k-line panel. Null with nothing ticked, so
 * the step is absent rather than reading "0 items selected".
 *
 * @returns {{ title: string, count: string, items: Array<{id: string, title: string}> }|null}
 */
export function selectionRailStep(picks, opts = {}) {
  const plural = opts.plural || 'Research packs'
  const items = (picks || [])
    .filter(p => p && p.id)
    .map(p => ({ id: String(p.id), title: String(p.title || opts.noun || 'Research pack') }))
  if (!items.length) return null
  return { title: plural, count: `${items.length} item${items.length === 1 ? '' : 's'} selected`, items }
}

/**
 * "2 Research packs selected" — the Briefing's heading over the selected list (#257, frame
 * 40008185:406044). It replaces the bare "Selected": the frame writes the COUNT and the TYPE, which
 * is what makes the list under it legible at a glance beside the "3 Research packs" filter crumb
 * right above — "Selected" alone left the reader counting rows to tell the two numbers apart.
 */
export function selectedHeading(count, opts = {}) {
  const n = Number(count) || 0
  const noun = opts.noun || 'Research pack'
  const plural = opts.plural || `${noun}s`
  return `${n} ${n === 1 ? noun : plural} selected`
}

/**
 * What a one-pack comparison is CALLED — "Prada Sunglasses vs the web".
 *
 * The same shape as `comparisonTitle`'s "A vs B", because it is the same question with the web
 * standing in for the second pack. It exists because the ASK is a paragraph of table instructions:
 * without a title the middle's question chip, the rail's "Search opened" and the pack name a save
 * proposes would all read back that paragraph.
 */
export function internetCompareTitle(pick) {
  const title = ((pick && pick.title) || '').trim()
  return title ? `${title} vs the web` : 'Compared with the web'
}

/**
 * The web question for a ONE-pack comparison: what is saved in the pack, measured against what is
 * available online.
 *
 * Same answer SHAPE as the pack-to-pack prompt (a table then a recommendation), because the user is
 * asking the same question — "which of these should I pick?" — with the alternatives coming from
 * the web instead of from a second pack. It runs as a normal research ask rather than through the
 * comparison store: that store's result saves into TWO packs or more (the server enforces it), while
 * this answer belongs in the middle, where any part of it can be ticked into the pack it is about.
 */
export function buildInternetComparePrompt(pick, research) {
  if (!pick || !pick.id) return ''
  const title = (pick.title || 'this Research Pack').trim()
  const saved = packContext(research, pick.id)
  return [
    `Compare "${title}" with the best alternatives available online right now.`,
    'Present a markdown table with the columns Brand, Price, Quality, Reviews, Pros, Cons — one row per option,',
    `the first row being "${title}" — followed by a short "Recommendation" section with one line for each option.`,
    // The marker now FOLLOWS the name (Pouria, busy-my-web#192, superseding #127/#177) everywhere
    // it is shown, and the answer's own recommendation lines are one of those places — asking for
    // the order here is the only way to get it, since the model writes the line, not a template.
    'End the recommended option\'s line with " — Recommended" after its name; leave the other lines unmarked.',
    saved ? `What I saved about ${title}: ${saved}` : '',
  ].filter(Boolean).join('\n')
}

/**
 * The rail's LIVE comparison row while one is being made (#77, frames 40006822:264783 and
 * 40006824:265349).
 *
 * The frames put the comparison on the right-hand timeline from the moment Compare is pressed:
 * one row titled with the comparison ("Prada vs Louis Vuitton") whose second line reads
 * "Comparison started" while it runs and "Comparison ready" once the answer is in. Until the
 * result is SAVED there is no server event to build a step from — the comparison exists only in
 * the store — so this row is derived from the store's own status, exactly like the research rail's
 * "Search opened".
 *
 * Nothing is drawn before it starts or after it fails: the frames have no row for either, and a
 * rail row is a record of something that happened.
 *
 * @returns {{ key, svg, color, title, sub }|null}
 */
export function liveComparisonStep(status, title) {
  const sub = { running: 'Comparison started', done: 'Comparison ready' }[status]
  if (!sub) return null
  return {
    key: 'comparison:live',
    // The frames draw it with the same globe the web-research rows carry — a comparison IS a web
    // ask — in the app's search blue.
    svg: 'global',
    color: '#388bff',
    title: String(title || '').trim() || 'Comparison',
    sub,
  }
}

// "Prada Sunglasses" + "Louis Vuitton Sunglasses" → "Prada vs Louis Vuitton", and with a third
// pack "Prada vs Louis Vuitton vs Gucci": drop the trailing words ALL the titles share (keeping at
// least one word each) — the design's short title, now over any number of packs.
export function comparisonTitle(picks) {
  const titles = (picks || []).map(p => ((p && p.title) || '').trim()).filter(Boolean)
  if (titles.length < 2) return titles[0] || 'Comparison'
  const words = titles.map(t => t.split(/\s+/))
  let drop = 0
  while (
    words.every(w => drop < w.length - 1) &&
    words.every(w => w[w.length - 1 - drop].toLowerCase() === words[0][words[0].length - 1 - drop].toLowerCase())
  ) drop++
  return words.map(w => w.slice(0, w.length - drop).join(' ')).join(' vs ')
}

// How much of a pack's write-up the AI is given when that is all the pack has — enough to describe
// what was researched, short enough that one pack cannot fill the whole prompt.
const DOC_CONTEXT_CHARS = 600

/**
 * What the AI is told a pack holds.
 *
 * Normally its saved items, exactly as before — the write-up is written FROM those, so repeating it
 * would say everything twice. A pack with NO items of its own is the case `packHasResearch` now
 * lets through: there the document is all there is, so that is what the question carries. Without
 * it the comparison would have asked about two pack NAMES and nothing else.
 */
function packContext(research, packId) {
  if (packItemCount(research, packId) > 0) {
    return (research || [])
      .filter(r => r && r.kind !== 'doc' && r.kind !== 'comparison' && itemInPack(r, packId))
      .map(itemLine).map(s => s.trim()).filter(Boolean).slice(0, 10).join('; ')
  }
  // The pictures drop out here for the same reason `itemLine` skips photos: the AI is being told
  // what was researched, and "[image]" says nothing. They still COUNT as content upstream.
  return packDocText(research, packId)
    .replace(/\[image\]/g, ' ').replace(/\s+/g, ' ').trim()
    .slice(0, DOC_CONTEXT_CHARS).trim()
}

// One line describing a saved item for the comparison prompt (photos say nothing useful).
function itemLine(r) {
  if (!r) return ''
  if (r.kind === 'photo') return ''
  if (r.kind === 'link') return r.title || r.url || ''
  return String(r.content || r.title || '').slice(0, 160)
}

// "A" and "B" / "A", "B" and "C" — the packs named in the question, however many were picked.
function namedList(titles) {
  const quoted = titles.map(t => `"${t}"`)
  if (quoted.length < 3) return quoted.join(' and ')
  return `${quoted.slice(0, -1).join(', ')} and ${quoted[quoted.length - 1]}`
}

// The web question the comparison asks: the AI sees EVERY picked pack's saved research and answers
// with the comparison table + recommendation the design shows (frame 09) — one row per pack, so a
// three-way comparison is the same answer with a third row.
export function buildComparePrompt(picks, research) {
  const list = (picks || []).filter(p => p && p.id)
  if (list.length < MIN_COMPARE_PACKS) return ''
  const notesOf = (p) => packContext(research, p.id)
  return [
    `Compare ${namedList(list.map(p => p.title))}.`,
    'Present a markdown table with the columns Brand, Price, Quality, Reviews, Pros, Cons — one row per option —',
    'followed by a short "Recommendation" section with one line for each option.',
    // Marker after the name, as every other Recommended marker in the app now reads (#192, superseding #127/#177).
    'End the recommended option\'s line with " — Recommended" after its name; leave the other lines unmarked.',
    ...list.map(p => { const ctx = notesOf(p); return ctx ? `What I saved about ${p.title}: ${ctx}` : '' }),
  ].filter(Boolean).join('\n')
}

const esc = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

// Inline markdown: **bold** and [label](url) links. Input is escaped BEFORE any markup is added.
const inline = (line) => esc(line)
  .replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold text-[#111827]">$1</strong>')
  .replace(/\[([^\]]+)\]\((https?:[^)\s]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="font-medium text-[#3741f3] underline decoration-[#3741f3]/30 hover:decoration-[#3741f3]">$1</a>')

const isTableRow = (line) => /^\|.*\|$/.test(line)
const isTableSeparator = (line) => /^\|[\s:|-]+\|$/.test(line)
const rowCells = (line) => line.replace(/^\||\|$/g, '').split('|').map(c => inline(c.trim()))

// Markdown → HTML for the comparison result. Same idea as the research answer renderer, plus
// TABLES — the comparison IS a table (frame 09).
export function comparisonHtml(md) {
  const lines = String(md || '').split('\n')
  let html = ''
  let inList = false
  const closeList = () => { if (inList) { html += '</ul>'; inList = false } }
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim()
    if (!line) { closeList(); continue }
    // Table block: a header row, a |---| separator, then the body rows.
    if (isTableRow(line) && isTableSeparator((lines[i + 1] || '').trim())) {
      closeList()
      const head = rowCells(line)
      const rows = []
      let j = i + 2
      while (j < lines.length && isTableRow(lines[j].trim())) { rows.push(rowCells(lines[j].trim())); j++ }
      html += '<div class="my-3 overflow-x-auto rounded-xl border border-black/[0.06] shadow-[0_1px_8px_rgba(0,0,0,0.04)]">'
        + '<table class="w-full border-collapse text-[13px] leading-[1.6]">'
        + `<thead><tr class="bg-[#f9fafb] text-left text-[#4b5563]">${head.map(h => `<th class="px-3 py-2 font-medium">${h}</th>`).join('')}</tr></thead>`
        + `<tbody>${rows.map(r => `<tr>${r.map(c => `<td class="border-t border-black/[0.04] px-3 py-2.5 align-top text-[#111827]">${c}</td>`).join('')}</tr>`).join('')}</tbody>`
        + '</table></div>'
      i = j - 1
      continue
    }
    if (/^[-*•]\s+/.test(line)) {
      if (!inList) { html += '<ul class="ml-5 list-disc space-y-1">'; inList = true }
      html += `<li>${inline(line.replace(/^[-*•]\s+/, ''))}</li>`
      continue
    }
    if (/^#{1,6}\s+/.test(line)) {
      closeList()
      html += `<p class="mt-4 text-[16px] font-semibold text-[#111827]">${inline(line.replace(/^#{1,6}\s+/, ''))}</p>`
      continue
    }
    closeList()
    html += `<p class="my-1">${inline(line)}</p>`
  }
  closeList()
  return html
}
